import { useCallback, useEffect, useRef, useState } from 'react'
import * as signalR from '@microsoft/signalr'
import { useAppSelector } from '../../../redux/hooks'
import { selectId } from '../../auth/auth.slice'
import { Config } from '../../../redux/config'

const HUB_ENDPOINT = '/notifications'
const METHOD_JOIN_GROUP = 'JoinGroup'
const PROGRESS_EVENT = 'invoicingProgress'

export type InvoicingProgress = {
    runId: string
    processed: number
    total: number
    companyName: string
    outcome: string
    completed: boolean
}

/**
 * Opens a SignalR connection for the lifetime of the host component, joins the current
 * user's group and listens for "invoicingProgress" events emitted by the backend during
 * a batch invoicing run. Exposes the latest progress plus a reset() to clear it between runs.
 */
export const useInvoicingProgress = () => {
    const userId = useAppSelector(selectId)
    const [progress, setProgress] = useState<InvoicingProgress | null>(null)
    const connectionRef = useRef<signalR.HubConnection | null>(null)

    useEffect(() => {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(Config.backendUrl + HUB_ENDPOINT, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .build()
        connectionRef.current = connection

        connection
            .start()
            .then(() => connection.send(METHOD_JOIN_GROUP, userId))
            .catch((err) => console.error('Error connecting to SignalR Hub:', err))

        connection.on(PROGRESS_EVENT, (payload: InvoicingProgress) => {
            setProgress(payload)
        })

        return () => {
            connection.stop().catch(() => undefined)
            connectionRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const reset = useCallback(() => setProgress(null), [])

    return { progress, reset }
}
